// v4 storage — a storage barn with an open yard: a timber barn under a
// hazard-yellow (ROOF_LOGI) gable with wide double doors on the street face,
// and an open east-half yard of crates, barrels and sacks. The v4 polish
// uses ROOF_LOGI for the gable instead of the ROOF_HOME terracotta the v3
// borrowed (a barn stores goods, it is not a home).
//
// Ported 1:1 from age-of-mcp's v4/storage.ts, using the library's shared
// v3 style helpers (`styleV3`).
import { defineModel, styleV3, type Part } from "../../lib";

const { WOOD, WOOD_DK, TIMBER, CANVAS, ROOF_LOGI, box, cyl, slab, poles, gable } = styleV3;

const BASE   = 0.12;
const BX     = -0.45;
const BARN_W = 0.95;
const BARN_D = 1.5;
const WALL_H = 0.9;
const FRONT  = BARN_D / 2;

function barn(h: number): Part {
  return box(BARN_W, h, BARN_D, WOOD, [BX, BASE + h / 2, 0]);
}

function partsFor(s: number): Part[] {
  if (s <= 0) return [slab(1.94, 1.94)];
  if (s === 1) return [slab(1.94, 1.94), barn(0.4), ...poles(1.9, 1.9, 0.85)];
  if (s === 2) return [slab(1.94, 1.94), barn(WALL_H), ...poles(1.9, 1.9, 1.5)];

  const top = BASE + WALL_H;
  return [
    slab(1.94, 1.94),
    barn(WALL_H),
    // Hazard-yellow gable — logistics & fuel, the correct function code.
    gable(1.12, 1.62, 0.55, ROOF_LOGI, BX, top, 0, "z"),
    // Wide double doors with an oak lintel on the street face.
    box(0.55, 0.62, 0.05, WOOD_DK, [BX, BASE + 0.31, FRONT + 0.01]),
    box(0.62, 0.06, 0.06, TIMBER,  [BX, BASE + 0.65, FRONT + 0.01]),
    box(0.05, 0.62, 0.06, TIMBER,  [BX, BASE + 0.31, FRONT + 0.015]),
    // THE YARD (east half, open to the sky): crates, barrels, sacks.
    box(0.4, 0.4, 0.4,    WOOD,    [0.42, BASE + 0.2, -0.5]),
    box(0.34, 0.34, 0.34, WOOD_DK, [0.42, BASE + 0.57, -0.5]),
    box(0.32, 0.32, 0.32, WOOD,    [0.8, BASE + 0.16, -0.42]),
    cyl(0.16, 0.15, 0.34, 8, WOOD_DK, [0.35, BASE + 0.17, 0.3]),
    cyl(0.14, 0.13, 0.3, 8,  WOOD_DK, [0.68, BASE + 0.15, 0.45]),
    cyl(0.13, 0.17, 0.24, 6, CANVAS, [0.76, BASE + 0.12, 0.02]),
    cyl(0.11, 0.15, 0.2, 6,  CANVAS, [0.5, BASE + 0.1, -0.08]),
  ];
}

export const storageV4 = defineModel({
  id: "storage-v4",
  tiles: { w: 2, h: 2 },
  batchId: "polished",
  name: "Storage (v4)",
  tags: ["building","storage","low-poly","age-of-mcp","v4"],
  summary: "Low-poly storage.",
  description: "A walled yard of stacked crates under slanted plank roofs, holding far more goods than a chest.",
  author: "age-of-mcp",
  footprint: 1,
  defaultVariant: "complete",
  variants: [
    { id: "stage-0", label: "Foundation", parts: () => partsFor(0) },
    { id: "stage-1", label: "Lower", parts: () => partsFor(1) },
    { id: "stage-2", label: "Upper", parts: () => partsFor(2) },
    { id: "complete", label: "Complete", parts: () => partsFor(3) },
  ],
  animations: [],
});
